(() => {
  if (window.rdbOccasionTabs) return;
  window.rdbOccasionTabs = true;
  const mounted = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const setup = (scope = document) => scope.querySelectorAll('[data-occasion-tabs]').forEach(widget => {
    if (mounted.has(widget)) return;
    const band = widget.querySelector('[data-occasion-band]');
    const tabs = [...band.querySelectorAll('[data-occasion-tab]')];
    const panels = tabs.map(tab => document.getElementById(tab.dataset.panel));
    if (!tabs.length || panels.some(panel => !panel || !widget.contains(panel))) return;
    const controller = new AbortController();
    const on = (target, type, listener, options = {}) => target.addEventListener(type, listener, { ...options, signal: controller.signal });
    const direction = () => getComputedStyle(band).direction === 'rtl' ? -1 : 1;
    const behavior = () => reduced.matches ? 'instant' : 'smooth';
    const arrows = widget.querySelector('[data-occasion-arrows]');
    const previous = arrows.querySelector('[data-occasion-previous]');
    const next = arrows.querySelector('[data-occasion-next]');
    const cue = widget.querySelector('[data-occasion-cue]');
    let drag = null, suppressUntil = 0;
    const update = () => {
      const max = band.scrollWidth - band.clientWidth;
      arrows.hidden = max < 2;
      previous.disabled = Math.abs(band.scrollLeft) < 2;
      next.disabled = Math.abs(band.scrollLeft) >= max - 2;
      if (cue) {
        const hide = max < 2 || next.disabled;
        if (hide && document.activeElement === cue) tabs[tabs.length - 1].focus({ preventScroll: true });
        cue.hidden = hide;
      }
    };
    const reveal = tab => {
      const item = tab.getBoundingClientRect(), viewport = band.getBoundingClientRect();
      const reserve = cue && !cue.hidden && getComputedStyle(cue).display !== 'none' ? 48 : 0;
      const left = viewport.left + (direction() < 0 ? reserve : 0), right = viewport.right - (direction() > 0 ? reserve : 0);
      const delta = item.left < left ? item.left - left - 4 : item.right > right ? item.right - right + 4 : 0;
      if (delta) band.scrollBy({ left: delta, behavior: behavior() });
    };
    const activate = (index, focus = false, scroll = true) => {
      tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        panels[i].hidden = i !== index;
      });
      const panel = panels[index], template = panel.querySelector('template[data-occasion-products]');
      if (template) { template.replaceWith(template.content.cloneNode(true)); }
      panel.dispatchEvent(new CustomEvent('rdb:products-filtered'));
      if (focus) tabs[index].focus({ preventScroll: true });
      if (scroll) reveal(tabs[index]);
    };
    band.setAttribute('role', 'tablist');
    tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panels[index].id);
      panels[index].setAttribute('role', 'tabpanel');
      on(tab, 'click', event => { event.preventDefault(); activate(index); });
      on(tab, 'keydown', event => {
        let nextIndex;
        if (event.key === 'ArrowRight') nextIndex = index + direction();
        else if (event.key === 'ArrowLeft') nextIndex = index - direction();
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = tabs.length - 1;
        else if (event.key === ' ') nextIndex = index;
        else return;
        event.preventDefault(); activate((nextIndex + tabs.length) % tabs.length, true);
      });
    });
    on(previous, 'click', () => band.scrollBy({ left: -direction() * band.clientWidth * .75, behavior: behavior() }));
    on(next, 'click', () => band.scrollBy({ left: direction() * band.clientWidth * .75, behavior: behavior() }));
    if (cue) on(cue, 'click', () => band.scrollBy({ left: direction() * band.clientWidth * .75, behavior: behavior() }));
    on(band, 'scroll', update, { passive: true });
    // Touch uses native scrolling, including vertical page scrolling. Mouse drag
    // follows the existing product rails, with click suppression after a drag.
    on(band, 'dragstart', event => event.preventDefault());
    on(band, 'pointerdown', event => {
      if (event.pointerType === 'mouse' && event.button === 0) drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: band.scrollLeft, active: false };
    });
    on(band, 'pointermove', event => {
      if (!drag) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.active && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
        drag.active = true; band.classList.add('is-dragging'); band.setPointerCapture(drag.id);
      }
      if (drag.active) { event.preventDefault(); band.scrollLeft = drag.left - dx; }
    });
    const endDrag = () => {
      const ending = drag; drag = null;
      if (ending?.active) {
        suppressUntil = performance.now() + 200; band.classList.remove('is-dragging');
        if (band.hasPointerCapture(ending.id)) band.releasePointerCapture(ending.id);
      }
    };
    on(band, 'pointerup', endDrag); on(band, 'pointercancel', endDrag); on(band, 'lostpointercapture', endDrag);
    on(band, 'pointerleave', () => { if (!drag?.active) drag = null; });
    on(band, 'click', event => {
      if (performance.now() < suppressUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, { capture: true });
    on(document, 'shopify:block:select', event => {
      const index = tabs.findIndex(tab => tab === event.target || tab.contains(event.target));
      if (index >= 0) activate(index);
    });
    const resize = new ResizeObserver(update); resize.observe(band);
    activate(0, false, false); update();
    document.fonts.ready.then(() => { if (widget.isConnected) update(); });
    mounted.set(widget, () => { controller.abort(); resize.disconnect(); });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup(), { once: true });
  else setup();
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [widget, cleanup] of mounted) if (event.target.contains(widget)) { cleanup(); mounted.delete(widget); }
  });
})();
